import React, { useEffect, useState } from "react";
import { Sparkles, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { logger } from "../lib/logger";

const VIBES = ["Cozy", "Adventurous", "Low-key", "Romantic"];

export default function LiveDateIdeas({ sessionId, className = "" }) {
    const [enabled, setEnabled] = useState(false);
    const [vibe, setVibe] = useState("Cozy");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        api.researchStatus().then(s => setEnabled(!!s.enabled)).catch(() => setEnabled(false));
    }, []);

    if (!enabled) return null;

    const run = async () => {
        setLoading(true);
        try {
            setResult(await api.dateIdeas(sessionId, vibe));
        } catch (err) {
            logger.warn("date ideas failed:", err);
            toast.error(err?.response?.data?.detail || "Couldn't fetch live ideas right now.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div data-testid="live-date-ideas" className={`border border-burgundy/15 bg-cream-soft p-4 space-y-3 ${className}`}>
            <div className="flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-burgundy">
                <Sparkles size={13} /> Live ideas near you
            </div>
            <div className="flex flex-wrap gap-2">
                {VIBES.map(v => (
                    <button
                        key={v}
                        type="button"
                        data-testid={`vibe-${v.toLowerCase()}`}
                        onClick={() => setVibe(v)}
                        className={`px-3 min-h-[36px] text-[10px] tracking-[0.22em] uppercase border transition-colors ${
                            vibe === v ? "bg-burgundy text-cream border-burgundy" : "border-burgundy/25 text-ink-muted"
                        }`}
                    >
                        {v}
                    </button>
                ))}
            </div>
            <button
                type="button"
                data-testid="live-ideas-btn"
                onClick={run}
                disabled={loading}
                className="w-full min-h-[44px] bg-burgundy text-cream text-[10px] tracking-[0.24em] uppercase disabled:opacity-50 flex items-center justify-center gap-2"
            >
                {loading ? <Loader2 size={14} className="animate-spin" /> : null}
                {loading ? "Searching the web" : "Find tonight's best spots"}
            </button>
            {result && (
                <div className="space-y-3 pt-1" data-testid="live-ideas-result">
                    <p className="text-[10px] tracking-[0.24em] uppercase text-ink-light">{result.city}</p>
                    <p className="text-sm leading-relaxed text-ink whitespace-pre-line">{result.answer}</p>
                    {result.citations?.length > 0 && (
                        <ul className="space-y-1.5">
                            {result.citations.map((c, i) => (
                                <li key={c.url}>
                                    <a href={c.url} target="_blank" rel="noopener noreferrer" data-testid={`citation-${i}`}
                                       className="tap-link inline-flex items-center gap-1.5 text-xs text-burgundy underline underline-offset-4 break-all">
                                        <ExternalLink size={11} className="shrink-0" />
                                        {c.title || new URL(c.url).hostname}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
}
